import React, { useEffect, useState, useCallback, useMemo } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { Container } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../../services/API';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import sortFunc from '../../../utils/ordernarColunaInt';
import ModalEditarLoja from './ModalEditarLoja';

const ToSearch = (props) => {
  let input;
  const handleClick = () => {
    props.onSearch(input.value);
  };

  const handleEnterKey = (e) => {
    if (e.key === 'Enter') {
      props.onSearch(input.value);
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center">
      <input
        className="input-theme busca mr-1"
        ref={(n) => (input = n)}
        type="text"
        onKeyUp={handleEnterKey}
      />
      <button
        type="button"
        className="btn-primario-slim"
        onClick={handleClick}
        style={{ width: 40, height: 40 }}
      >
        <i className="fa fa-search px-2" />
      </button>
    </div>
  );
};

const defaultSorted = [
  {
    dataField: 'totalPontuado',
    order: 'desc',
  },
];

export default function Revendas() {
  const [data, setData] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedRevenda, setSelectedRevenda] = useState(null);
  const [updatingRm, setUpdatingRm] = useState({});

  useEffect(() => {
    buscarRevendas();
  }, []);

  const handleEdit = (revenda) => {
    setSelectedRevenda(revenda);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedRevenda(null);
  };

  const buscarRevendas = async () => {
    setBuscando(true);
    const resultado = await api.get('/api/revendas/painel');
    if (resultado) {
      setData(resultado.data.revendas);
    }
    setBuscando(false);
  };

  const handleToggleRm = useCallback(async (revendaId, currentRmStatus) => {
    setUpdatingRm(prev => ({ ...prev, [revendaId]: true }));
    
    const novoValorRm = !currentRmStatus;
    
    try {
      const resultado = await api.put(`/api/revendas/${revendaId}`, {
        rm: novoValorRm,
      });
      
      if (resultado && resultado.data) {
        setUpdatingRm(prev => {
          const newState = { ...prev };
          delete newState[revendaId];
          return newState;
        });
        
        setData(prevData =>
          prevData.map(revenda =>
            revenda.id === revendaId
              ? { ...revenda, rm: resultado.data.rm }
              : revenda
          )
        );
        
        toast.success(
          resultado.data.rm
            ? 'Revenda Master ativada com sucesso!' 
            : 'Revenda Master desativada com sucesso!',
          {
            position: toast.POSITION.BOTTOM_CENTER,
            autoClose: 5000,
          }
        );
      }
    } catch (error) {
      setUpdatingRm(prev => {
        const newState = { ...prev };
        delete newState[revendaId];
        return newState;
      });
      toast.error('Erro ao atualizar status de Revenda Master', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 5000,
      });
    }
  }, []);

  const columns = useMemo(() => [
    {
      dataField: 'nomeInput',
      text: 'Revenda',
      sort: true,
      formatter: (celula, valor) => {
        return (valor.nomeInput || '').toUpperCase();
      },
      style: { textAlign: 'left' },
      headerStyle: { textAlign: 'left' },
    },
    {
      dataField: 'cnpj',
      text: 'CNPJ',
      sort: true,
      formatter: (celula, valor) => {
        const cnpj = (valor.cnpj || '').replace(/[^\d]/g, '');
        return cnpj.replace(
          /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
          '$1.$2.$3/$4-$5'
        );
      },
    },
    {
      dataField: 'endCidade',
      text: 'Cidade / UF',
      sort: true,
      formatter: (celula, valor) =>
        [valor.endCidade, valor.endEstado].filter(Boolean).join(' / '),
    },
    {
      dataField: 'endBairro',
      text: 'Bairro',
      sort: true,
      formatter: (celula, valor) =>
        [valor.endBairro, valor.endComplemento].filter(Boolean).join(' - '),
    },
    {
      dataField: 'totalVendido',
      text: 'Total Vendido',
      sort: true,
      sortFunc: sortFunc,
      formatter: (celula, valor) => {
        const totalVendido = Number(valor.totalVendido);
        return (Number.isFinite(totalVendido) ? totalVendido : 0).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        });
      },
    },
    {
      dataField: 'totalPontuado',
      text: 'Total Pontuado',
      sort: true,
      sortFunc: sortFunc,
      formatter: (celula, valor) =>
        valor.totalPontuado
          ? Number(valor.totalPontuado).toLocaleString('pt-BR')
          : 0,
    },
    {
      dataField: 'saldoPontos',
      text: 'Saldo',
      sort: true,
      sortFunc: sortFunc,
      formatter: (celula, valor) =>
        valor.saldoPontos ? Number(valor.saldoPontos).toLocaleString('pt-BR') : 0,
    },
    {
      dataField: 'pintoresIndicados',
      text: 'Pintores Indicados',
      sort: true,
      sortFunc: sortFunc,
    },
    {
      dataField: 'fatorPontuacao',
      text: 'Fator',
      sort: true,
      formatter: (celula, valor) => `${valor.fatorPontuacao || 1}x`,
    },
    {
      dataField: 'rm',
      text: 'RM',
      sort: true,
      formatter: (celula, row) => {
        const isRm = Boolean(row.rm);
        const isDisabled = updatingRm[row.id];
        return (
          <div className="d-flex justify-content-center align-items-center">
            <label
              className="switch mb-0"
              style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
            >
              <input
                type="checkbox"
                checked={isRm}
                onChange={() => handleToggleRm(row.id, isRm)}
                disabled={isDisabled}
              />
              <span className="slider round"></span>
            </label>
          </div>
        );
      },
      headerStyle: { width: '80px' },
    },
    {
      dataField: 'actions',
      text: 'Ações',
      formatter: (celula, row) => (
        <button
          type="button"
          className="btn-primario-slim"
          onClick={() => handleEdit(row)}
        >
          <i className="fas fa-edit" />
        </button>
      ),
    },
  ], [updatingRm, handleToggleRm]);

  const options = {
    sizePerPage: 10,
    showTotal: true,
    sizePerPageList: [
      {
        text: '5',
        value: 5,
      },
      {
        text: '10',
        value: 10,
      },
      {
        text: '50',
        value: 50,
      },
      {
        text: '100',
        value: 100,
      },
      {
        text: 'Todos',
        value: data.length,
      },
    ],
    paginationTotalRenderer: (from, to, size) =>
      size > 0 ? (
        <span className="react-bootstrap-table-pagination-total px-2">
          {from}-{to} de {size} resultados
        </span>
      ) : (
        <span className="react-bootstrap-table-pagination-total px-2">
          Nenhum resultado
        </span>
      ),
  };

  return (
    <Container fluid>
      <h4 className="pt-3 pb-3 f-black">Revendas</h4>
      <hr className="hr-loja mt-0 mb-4" />
      <div>
        <ToolkitProvider
          bootstrap4
          keyField="id"
          data={data}
          columns={columns}
          search
        >
          {(props) => (
            <div>
              <div className="d-flex flex-row-reverse mb-2">
                <ToSearch {...props.searchProps} />
              </div>
              <BootstrapTable
                noDataIndication={() =>
                  buscando ? (
                    <span>
                      Carregando
                      <i className="fas fa-circle-notch fa-spin ml-3" />
                    </span>
                  ) : (
                    <span>Sem resultados</span>
                  )
                }
                classes="table-dark text-center rounded"
                wrapperClasses="rounded"
                bordered={false}
                {...props.baseProps}
                pagination={paginationFactory(options)}
                defaultSorted={defaultSorted}
              />
            </div>
          )}
        </ToolkitProvider>
      </div>
      
      <ModalEditarLoja
        show={showModal}
        onClose={handleClose}
        revenda={selectedRevenda}
        onSuccess={buscarRevendas}
      />
    </Container>
  );
}
